import { useState } from "react";
import { toast } from "sonner";
import { User, Bell, Globe, Trash2, Save, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";

const SECTIONS = [
    { id: "profile", label: "Profile", icon: User },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "preferences", label: "Preferences", icon: Globe },
    { id: "security", label: "Security", icon: Shield },
];

export default function Settings() {
    const { profile, updatePassword, updateProfile } = useAuth();
    const [section, setSection] = useState("profile");
    const [fullName, setFullName] = useState(profile?.full_name || "");
    const [syncedName, setSyncedName] = useState(profile?.full_name);
    const [saving, setSaving] = useState(false);
    const [newPassword, setNewPassword] = useState("");

    // Sync local fullName when profile loads / changes (avoids useEffect-set-state lint)
    if (profile?.full_name !== syncedName) {
        setSyncedName(profile?.full_name);
        setFullName(profile?.full_name || "");
    }

    const saveProfile = async () => {
        if (!fullName.trim()) {
            toast.error("Name cannot be empty");
            return;
        }
        setSaving(true);
        try {
            await updateProfile({ full_name: fullName.trim() });
            toast.success("Profile updated");
        } catch (err) {
            toast.error(err.message || "Could not update profile");
        } finally {
            setSaving(false);
        }
    };

    const savePassword = async () => {
        if (newPassword.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        setSaving(true);
        try {
            await updatePassword(newPassword);
            toast.success("Password updated");
            setNewPassword("");
        } catch (err) {
            toast.error(err.message || "Could not update password");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
                    Settings
                </h1>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                    Manage your account and preferences.
                </p>
            </div>

            <div className="grid lg:grid-cols-[220px_1fr] gap-6">
                <nav className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-2 h-fit">
                    {SECTIONS.map((s) => (
                        <button
                            key={s.id}
                            onClick={() => setSection(s.id)}
                            className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                section === s.id
                                    ? "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300"
                                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
                            }`}
                        >
                            <s.icon className="h-4 w-4" />
                            {s.label}
                        </button>
                    ))}
                </nav>

                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 lg:p-7 space-y-6">
                    {section === "profile" && (
                        <>
                            <div>
                                <h3 className="font-semibold text-slate-900 dark:text-slate-50">
                                    Profile Information
                                </h3>
                                <p className="text-sm text-slate-500 mt-1">
                                    This appears across your dashboard and reports.
                                </p>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs font-semibold">Full Name</Label>
                                    <Input
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        className="mt-1.5"
                                        data-testid="settings-name-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold">Email</Label>
                                    <Input
                                        value={profile?.email || ""}
                                        readOnly
                                        className="mt-1.5 opacity-70 cursor-not-allowed"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label className="text-xs font-semibold">Plan</Label>
                                    <Input
                                        value={profile?.plan || "free"}
                                        readOnly
                                        className="mt-1.5 opacity-70 cursor-not-allowed capitalize"
                                    />
                                </div>
                            </div>
                            <Button
                                onClick={saveProfile}
                                disabled={saving}
                                className="bg-indigo-600 hover:bg-indigo-700"
                                data-testid="settings-save-btn"
                            >
                                <Save className="h-4 w-4 mr-2" />
                                {saving ? "Saving…" : "Save Changes"}
                            </Button>
                        </>
                    )}
                    {section === "notifications" && (
                        <>
                            <div>
                                <h3 className="font-semibold text-slate-900 dark:text-slate-50">
                                    Email Notifications
                                </h3>
                                <p className="text-sm text-slate-500 mt-1">
                                    Choose what you want to hear about.
                                </p>
                            </div>
                            {[
                                { l: "Analysis completed", d: "When a tender finishes processing", on: true },
                                { l: "Credit warnings", d: "When 80% credits are used", on: true },
                                { l: "Weekly summary", d: "Every Monday at 9am IST", on: false },
                                { l: "Product updates", d: "New features and improvements", on: true },
                                { l: "Marketing emails", d: "Tips, guides, case studies", on: false },
                            ].map((n) => (
                                <div
                                    key={n.l}
                                    className="flex items-start justify-between py-3 border-t border-slate-100 dark:border-slate-800 first:border-0"
                                >
                                    <div>
                                        <p className="font-medium text-sm text-slate-900 dark:text-slate-50">
                                            {n.l}
                                        </p>
                                        <p className="text-xs text-slate-500">{n.d}</p>
                                    </div>
                                    <Switch defaultChecked={n.on} />
                                </div>
                            ))}
                        </>
                    )}
                    {section === "preferences" && (
                        <>
                            <div>
                                <h3 className="font-semibold text-slate-900 dark:text-slate-50">
                                    Preferences
                                </h3>
                                <p className="text-sm text-slate-500 mt-1">
                                    Language, currency, and report format.
                                </p>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs font-semibold">Language</Label>
                                    <Select defaultValue="en">
                                        <SelectTrigger className="mt-1.5">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="en">English</SelectItem>
                                            <SelectItem value="hi">हिन्दी (Hindi)</SelectItem>
                                            <SelectItem value="bn">বাংলা (Bengali)</SelectItem>
                                            <SelectItem value="ta">தமிழ் (Tamil)</SelectItem>
                                            <SelectItem value="te">తెలుగు (Telugu)</SelectItem>
                                            <SelectItem value="mr">मराठी (Marathi)</SelectItem>
                                            <SelectItem value="gu">ગુજરાતી (Gujarati)</SelectItem>
                                            <SelectItem value="kn">ಕನ್ನಡ (Kannada)</SelectItem>
                                            <SelectItem value="ml">മലയാളം (Malayalam)</SelectItem>
                                            <SelectItem value="pa">ਪੰਜਾਬੀ (Punjabi)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold">Timezone</Label>
                                    <Select defaultValue="ist">
                                        <SelectTrigger className="mt-1.5">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="ist">
                                                IST (UTC+5:30) — Kolkata
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <Button
                                onClick={() => toast.success("Preferences saved")}
                                className="bg-indigo-600 hover:bg-indigo-700"
                            >
                                <Save className="h-4 w-4 mr-2" /> Save Preferences
                            </Button>
                        </>
                    )}
                    {section === "security" && (
                        <>
                            <div>
                                <h3 className="font-semibold text-slate-900 dark:text-slate-50">
                                    Security
                                </h3>
                                <p className="text-sm text-slate-500 mt-1">
                                    Keep your account safe.
                                </p>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs font-semibold">
                                        New Password
                                    </Label>
                                    <Input
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="At least 8 characters"
                                        className="mt-1.5"
                                    />
                                </div>
                            </div>
                            <Button
                                onClick={savePassword}
                                disabled={saving}
                                className="bg-indigo-600 hover:bg-indigo-700"
                            >
                                {saving ? "Updating…" : "Update Password"}
                            </Button>

                            <div className="mt-8 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50/60 dark:bg-red-950/20 p-5">
                                <h4 className="font-semibold text-red-700 dark:text-red-300">
                                    Danger Zone
                                </h4>
                                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                                    Permanently delete your account and all data. This action
                                    cannot be undone.
                                </p>
                                <Button
                                    variant="outline"
                                    className="mt-4 border-red-300 text-red-700 hover:bg-red-100 dark:border-red-900/60 dark:bg-transparent dark:text-red-300"
                                >
                                    <Trash2 className="h-4 w-4 mr-2" /> Delete Account
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
